/**
 * GST ON EACH WORK ORDER LINE — asked by the CMD, 31-10-2026.
 *
 * The order header has always carried one GST figure (fn_wo_recalc). This
 * splits it back onto the lines the same way that function builds it, so the
 * per-line figures add up to the header, to the paisa:
 *
 *   line GST = round(line value x GST rate / 100, 2)
 *
 * WHO PAYS IT, per line - read from the order's own answer, never re-decided:
 *   CHARGED  the contractor charges it on his invoice; OVAL pays it to him
 *   RCM      reverse charge: OVAL pays it to the Government, not to him
 *   NONE     no tax arises
 * On an order that is all one or the other the header says which. Only an
 * order mixing both (the notified-list switch, 0146) needs the line's own
 * verdict from v_rcm_determination.
 *
 * THE SPLIT follows the header's treatment: CGST + SGST in halves for a supply
 * within the state, IGST for one across states. CGST takes the rounded half and
 * SGST the rest, so the two always add back to the line's GST.
 */

export type GstPayer = 'CHARGED' | 'RCM' | 'NONE'

export interface GstHeader {
  gst_treatment: string | null
  is_reverse_charge: boolean
  gst_amount: number
}

export interface GstSplit {
  value: number
  rate: number
  payer: GstPayer
  gst: number
  cgst: number
  sgst: number
  igst: number
  /** What the contractor is paid: value plus the GST he charges. */
  total: number
}

const n = (v: unknown) => Number(v ?? 0) || 0
const r2 = (v: number) => Math.round(v * 100) / 100

export function gstPayer(h: GstHeader, verdict?: string | null): GstPayer {
  const charged = n(h.gst_amount) > 0
  if (!h.is_reverse_charge) return charged ? 'CHARGED' : 'NONE'
  if (verdict === 'NO_TAX') return 'NONE'
  if (!charged) return 'RCM'
  return verdict === 'FORWARD' ? 'CHARGED' : 'RCM'
}

export function gstSplit(
  value: number, rate: number, payer: GstPayer, treatment: string | null,
): GstSplit {
  const v = r2(n(value))
  const gst = payer === 'NONE' ? 0 : r2(v * n(rate) / 100)
  let cgst = 0; let sgst = 0; let igst = 0
  if (payer === 'CHARGED') {
    if (treatment === 'IGST') igst = gst
    else { cgst = r2(gst / 2); sgst = r2(gst - cgst) }
  }
  return {
    value: v, rate: n(rate), payer, gst, cgst, sgst, igst,
    total: r2(v + (payer === 'CHARGED' ? gst : 0)),
  }
}

/** One machine's monthly (or daily) cost including the GST he charges. */
export function withGst(amount: number, rate: number, payer: GstPayer): number {
  return payer === 'CHARGED' ? r2(n(amount) * (1 + n(rate) / 100)) : r2(n(amount))
}
